import React, { useEffect } from 'react';
import dayjs from 'dayjs';
import { AnyAction } from 'redux';
import { connect } from 'react-redux';
import first from 'lodash/first';
import { RouteComponentProps } from 'react-router';

import {
  MuiAlert,
  MuiBox,
  MuiCard,
  MuiCardContent,
  MuiContainer,
  MuiDivider,
  MuiGrid,
  MuiTypography
} from 'theme/material-ui';

import DataLoader from 'components/UI/DataLoader/DataLoader';
import { Confirm } from 'components/ConfirmDialog/ConfirmDialog';
import Spinner from 'components/UI/Spinner/Spinner';

import { FontSize, FontWeight, IconSize } from 'modules/styles/variables';
import { dateAndTimeSelect } from 'modules/constants/booking';

import { AnalyticsEvent } from 'services/AnalyticsService';

import { RootState } from 'store/types';
import * as bookingTypes from 'store/booking/types';
import * as bookingActions from 'store/booking/actions';
import * as bookingSelectors from 'store/booking/selectors';
import * as routerSelectors from 'store/router/selectors';
import { closeTimeSlotListener } from 'store/booking/actions';

import { BookingDatePicker } from './DatePicker';
import { BookingLoading, BookingNoData } from '../components/sharedComponents';
import { BookingLocationCard, StyledScreen } from '../components/styled';
import { BookingStepActions, BookingStepAction } from '../components/BookingStepper';
import { useCheckBookingInfoEffect } from '../useCheckBookingInfoEffect';
import { formatTimeWithTimeZone } from 'modules/utils/DateUtils';
import { TimeSlots } from './TimeSlotSelect';
import useNavigationAnalytics from 'hooks/useNavigationAnalytics';
import { Color } from 'shared/src/modules/styles/colors';
import { getUSPhoneNumber } from 'shared/src/modules/utils/PhoneUtils';

export interface Props extends RouteComponentProps {
  appointmentDetails: bookingTypes.AppointmentDetails;
  locationTimeSlots: bookingTypes.TimeSlot[];
  timeSlotsByAppointmentDate: bookingTypes.TimeSlot[];
  isFetchingDataDeps: boolean;
  setAppointmentDate: typeof bookingActions.setAppointmentDate;
  setAppointmentTime: typeof bookingActions.setAppointmentTime;
  resetAppointmentTime: typeof bookingActions.resetAppointmentTime;
  lockTimeSlot: typeof bookingActions.lockTimeSlot;
  isLockingAppointment: boolean;
  isFetchingTimeSlots: boolean;
}

export function BookingDateAndTimeSelect({
  appointmentDetails,
  locationTimeSlots,
  timeSlotsByAppointmentDate,
  setAppointmentDate,
  setAppointmentTime,
  resetAppointmentTime,
  lockTimeSlot,
  isLockingAppointment,
  isFetchingTimeSlots,
  history
}: Props) {
  const isLoading = isLockingAppointment || isFetchingTimeSlots;
  useCheckBookingInfoEffect(appointmentDetails?.patient);

  // Selects the earliest date that has slots.
  useEffect(() => {
    if (locationTimeSlots?.length && !appointmentDetails.appointmentDate) {
      const earliestDate = first(
        [...locationTimeSlots].sort((t1, t2) => (dayjs(t1.datetime).isBefore(t2.datetime) ? -1 : 1))
      );

      if (earliestDate) {
        setAppointmentDate(dayjs(earliestDate.datetime).format('YYYY-MM-DD'));
      }
    }
  }, [locationTimeSlots]);

  // Resets selected time when selected date is changed.
  useEffect(() => {
    resetAppointmentTime();
  }, [appointmentDetails.appointmentDate]);

  const onNextClick = async () => {
    const lockAppointmentSlotArgs = {
      slotStartTime: appointmentDetails?.appointmentTime?.datetime,
      providerId: appointmentDetails?.doctor?.cernerResourceId?.toString()
    };

    const lockAppointmentResponse: AnyAction = await lockTimeSlot(lockAppointmentSlotArgs);

    if (lockAppointmentResponse.error || !lockAppointmentResponse.payload.data.success) {
      Confirm.show(
        dateAndTimeSelect.LOCK_APPOINTMENT.ERROR.title,
        dateAndTimeSelect.LOCK_APPOINTMENT.ERROR.description
      );
    } else {
      history.push('/u/get-care-now/booking/reason-for-visit');
    }
    closeTimeSlotListener();
  };

  const onPreviousClick = () => {
    closeTimeSlotListener();
    history.goBack();
  };

  const headerText = `Select an appointment time for ${appointmentDetails?.patient?.firstName}'s visit with Dr. ${appointmentDetails?.doctor?.displayName}:`;
  const boxInfoText = `Can't find the date or time you want? Please call the clinic ${
    appointmentDetails?.appointmentLocation?.phoneDisplay
      ? getUSPhoneNumber(appointmentDetails?.appointmentLocation?.phoneDisplay)
      : ''
  }`;

  return (
    <>
      <StyledScreen>
        <MuiContainer maxWidth="lg">
          <MuiBox my={3}>
            <MuiBox mb={1}>
              <MuiTypography variant="h4">Select An Appointment</MuiTypography>
            </MuiBox>
            <MuiBox mb={3}>
              <MuiDivider />
            </MuiBox>
            <MuiBox mb={3}>
              <MuiTypography fontSize={FontSize.large}>{headerText}</MuiTypography>
            </MuiBox>
            <DataLoader
              data={locationTimeSlots}
              loading={isLoading}
              renderLoading={() => (
                <MuiBox textAlign="center" paddingTop={3}>
                  <Spinner
                    size={IconSize.xLarge}
                    data-testid="booking-date-and-time-select-loading"
                  />
                  <BookingLoading />
                </MuiBox>
              )}
              renderNoData={() => (
                <BookingNoData message={dateAndTimeSelect.LOCATION_TIMES.NO_DATA.message} />
              )}
              renderData={data => (
                <MuiGrid container justify="center">
                  <MuiGrid item xs>
                    <MuiBox mb={3} mr={3}>
                      <BookingDatePicker
                        timeSlots={data}
                        onChange={date => {
                          if (date) {
                            setAppointmentDate(date.toDate());
                          }
                        }}
                        value={appointmentDetails.appointmentDate}
                      />
                    </MuiBox>
                  </MuiGrid>
                  <MuiGrid item xs>
                    <MuiBox mb={3}>
                      <DataLoader
                        data={timeSlotsByAppointmentDate}
                        loading={isLoading}
                        renderLoading={() => <BookingLoading />}
                        renderNoData={() => (
                          <BookingNoData
                            message={dateAndTimeSelect.LOCATION_TIMES.NO_DATA.message}
                          />
                        )}
                        renderData={() => {
                          const [slotsData] = timeSlotsByAppointmentDate;
                          const { timeSlots } = slotsData;
                          return (
                            <>
                              <BookingLocationCard>
                                <MuiCardContent>
                                  <MuiTypography>Available time slots:</MuiTypography>
                                </MuiCardContent>
                                <TimeSlots
                                  timeSlots={timeSlots}
                                  appointmentDetails={appointmentDetails}
                                  setAppointmentTime={setAppointmentTime}
                                />
                              </BookingLocationCard>
                            </>
                          );
                        }}
                      />
                    </MuiBox>
                  </MuiGrid>
                </MuiGrid>
              )}
            />
            <MuiGrid container xs>
              <MuiGrid item xs>
                {appointmentDetails?.appointmentTime ? (
                  <MuiBox paddingBottom={3}>
                    <MuiCard>
                      <MuiCardContent>
                        <MuiTypography>You have chosen</MuiTypography>
                        <MuiTypography fontSize={FontSize.large} fontWeight={FontWeight.bold}>
                          {appointmentDetails?.appointmentTime?.datetime &&
                            formatTimeWithTimeZone(
                              appointmentDetails?.appointmentTime?.datetime
                            )}{' '}
                          on
                        </MuiTypography>
                        <MuiTypography fontSize={FontSize.large} fontWeight={FontWeight.bold}>
                          {dayjs(appointmentDetails?.appointmentDate).format('dddd MMMM Do')}
                        </MuiTypography>
                      </MuiCardContent>
                    </MuiCard>
                  </MuiBox>
                ) : null}

                {!isLoading ? (
                  <MuiBox>
                    <MuiAlert
                      style={{
                        width: '100%',
                        border: `2px solid ${Color.MHlogoLightBlue}`,
                        alignItems: 'center'
                      }}
                      severity="info"
                    >
                      <MuiTypography>{boxInfoText}</MuiTypography>
                    </MuiAlert>
                  </MuiBox>
                ) : null}
              </MuiGrid>
            </MuiGrid>
          </MuiBox>
        </MuiContainer>
      </StyledScreen>

      <BookingStepActions>
        <BookingStepAction
          disabled={!appointmentDetails?.appointmentDate || !appointmentDetails?.appointmentTime}
          onClick={onNextClick}
          data-testid="next-button"
        />
        <BookingStepAction onClick={onPreviousClick} data-test-id="previous-button" />
      </BookingStepActions>
    </>
  );
}

const mapStateToProps = (state: RootState) => ({
  appointmentDetails: bookingSelectors.appointmentDetailsSelector(state),
  locationTimeSlots: bookingSelectors.timeSlotsDataSelector(state),
  timeSlotsByAppointmentDate: bookingSelectors.timeSlotsByAppointmentDateSelector(state),
  isLockingAppointment: bookingSelectors.lockedAppointmentIsLockingSelector(state),
  isFetchingTimeSlots: bookingSelectors.timeSlotsFetchingSelector(state),
  currentUrl: routerSelectors.currentLocationPathNameSelector(state),
  referringUrl: routerSelectors.previousLocationPathNameSelector(state)
});

const mapDispatchToProps = {
  setAppointmentDate: bookingActions.setAppointmentDate,
  setAppointmentTime: bookingActions.setAppointmentTime,
  resetAppointmentTime: bookingActions.resetAppointmentTime,
  lockTimeSlot: bookingActions.lockTimeSlot
};

export default connect(mapStateToProps, mapDispatchToProps)(BookingDateAndTimeSelect);
